import React, { useState } from "react";
import InputField from "./InputField";
import PhoneNumberField from "./PhoneNumberField";
import { IoIosArrowUp } from "react-icons/io";
import { Camera, RotateCcw, Trash2 } from "lucide-react";

const OtherDetailsForm = ({
  formData,
  handleFormChange,
  handlePhoneNumberChange,
  handleCodeChange,
  handleCountryChange,
  
  // Sender Image Props
  senderPreview,
  handleTakePhoto,
  isCameraOpen,
  cancelCamera,
  capturePhoto,
  switchCamera,
  facingMode,
  senderImageError,
  videoRef,
  canvasRef,
  onRemoveImage
}) => {
  const [isOpen, setIsOpen] = useState(true);
  const toggleOpen = () => setIsOpen((prev) => !prev);

  // Check if the image is a base64 string or a URL
  const isBase64Image = (str) => {
    return typeof str === 'string' && (str.startsWith('data:image/') || str.startsWith('blob:'));
  };

  // Function to get image source
  const getImageSrc = (image) => {
    if (!image) return null;
    if (isBase64Image(image)) {
      return image;
    } else {
      // If it's a URL from the server, you might need to prepend the base URL
      return image.startsWith('http') ? image : `${process.env.REACT_APP_API_URL || ''}${image}`;
    }
  };

  return (
    <div className="w-full border-gray-300 my-4">
      {/* Header with arrow icon */}
      <div
        className="flex flex-row items-center justify-between mb-4 cursor-pointer"
        onClick={toggleOpen}
      >
        <h2 className="text-xl font-semibold mb-2 md:mb-0 relative">
          Other Details
        </h2>

        {/* Horizontal Divider with Dots */}
        <div className="relative flex-1 mx-6 h-4">
          {/* The line (centered vertically) */}
          <div className="absolute top-1/2 w-full border-t border-gray-300 -translate-y-1/2" />
          {/* Left Dot */}
          <span className="absolute left-0 top-1/2 -translate-x-1/2 -translate-y-1/2 h-2 w-2 bg-gray-300 rounded-full" />
          {/* Right Dot */}
          <span className="absolute right-0 top-1/2 translate-x-1/2 -translate-y-1/2 h-2 w-2 bg-gray-300 rounded-full" />
        </div>

        <IoIosArrowUp
          size={24}
          className={`transition-transform duration-300 ${isOpen ? "rotate-0" : "rotate-180"}`}
        />
      </div>

      {/* Collapsible Content */}
      <div
        className={`overflow-hidden transition-all duration-300 ease-in-out ${isOpen ? "max-h-[5000px] opacity-100" : "max-h-0 opacity-0"}`}
      >
        {/* Sender's Image Section */}
        <div className="mb-6 px-1">
          <label className="block text-lg font-semibold text-gray-700 mb-4">
            Selfie verification <span className="text-red-500">*</span>
          </label>

          {senderImageError && (
            <p className="text-red-500 text-sm mb-3">{senderImageError}</p>
          )}

          {/* Image preview */}
          {senderPreview ? (
            <div className="flex flex-col items-center max-w-md mx-auto">
              <div className="relative">
                <img
                  src={getImageSrc(senderPreview)}
                  alt="Sender"
                  className="w-48 h-48 object-cover rounded-lg border-4 border-white shadow-lg mb-4"
                  onError={(e) => {
                    console.error('Image failed to load:', senderPreview);
                    e.target.style.display = 'none';
                  }}
                />
                {/* Remove image button */}
                <button
                  type="button"
                  onClick={onRemoveImage}
                  className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-2 hover:bg-red-600 transition-colors shadow-lg"
                  title="Remove photo"
                >
                  <Trash2 size={16} />
                </button>
              </div>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={handleTakePhoto}
                  className="px-4 py-2 bg-[#e72848] text-white rounded-md hover:bg-[#d62040] transition flex items-center gap-2 shadow-md"
                >
                  <Camera size={18} /> Take New Photo
                </button>
              </div>
              <p className="text-green-600 text-sm mt-2">
                ✓ Photo uploaded successfully
              </p>
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center border-2 border-dashed border-gray-300 rounded-lg p-8 bg-gray-50 max-w-md mx-auto">
              <Camera className="text-gray-400 mb-4" size={56} />
              <p className="text-gray-600 mb-2 text-lg font-medium text-center">
                Photo Required
              </p>
              <p className="text-gray-500 text-sm mb-6 text-center max-w-md">
                Take a clear, well-lit photo for profile verification. 
                {facingMode === "environment" ? " Use the back camera for best quality." : " Use the front camera for self-portrait."}
              </p>
              <button
                type="button"
                onClick={handleTakePhoto}
                className="px-6 py-3 bg-[#e72848] text-white rounded-lg hover:bg-[#d62040] transition flex items-center gap-2 shadow-lg font-medium"
              >
                <Camera size={20} /> Take Photo
              </button>
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-2 mb-6 px-1">
          <div className="md:col-span-1">
            <InputField
              label="Expected Education"
              name="expected_education"
              placeholder="Enter Detail Education"
              value={formData.expected_education || ""}
              onChange={handleFormChange}
              required={false}
            />
          </div>
        </div>
        
        {/* Person to Contact */}
        <h3 className="text-lg mb-1">Person To Contact</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-2 mb-6 px-1">
          <div className="md:col-span-1">
            <InputField
              label="Name"
              name="person_to_contact_name"
              placeholder="Enter Person to Contact Name"
              value={formData.person_to_contact_name}
              onChange={handleFormChange}
              required={false}
            />
          </div>
          <div className="md-col-span-1">
            <label className="block text-base font-medium text-gray-700 mb-1">
              Phone Number
            </label>
            <PhoneNumberField
              value={formData.person_to_contact_phone}
              setPhoneNumber={(newPhone) =>
                handlePhoneNumberChange("person_to_contact_phone", newPhone)
              }
              disabled={false}
              code={formData.person_to_contact_country_code}
              setCode={(newCode) =>
                handleCodeChange("person_to_contact_country_code", newCode)
              }
              country={formData.person_to_contact_country}
            />
          </div>
        </div>

        {/* Social Media Section */}
        <h3 className="text-lg mb-1">Social Media</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-2 mb-6 px-1">
          <InputField
            label="Instagram"
            name="instagram"
            placeholder="Enter Instagram username"
            value={formData.instagram || ""}
            onChange={handleFormChange}
            required={false}
          />
          <InputField
            label="LinkedIn"
            name="linkedin"
            placeholder="Enter LinkedIn username"
            value={formData.linkedin || ""}
            onChange={handleFormChange}
            required={false}
          />
          <InputField
            label="Snapchat"
            name="snapchat"
            placeholder="Enter Snapchat username"
            value={formData.snapchat || ""}
            onChange={handleFormChange}
            required={false}
          />
          <InputField
            label="Twitter"
            name="twitter"
            placeholder="Enter Twitter username"
            value={formData.twitter || ""}
            onChange={handleFormChange}
            required={false}
          />
          <InputField
            label="Facebook"
            name="facebook"
            placeholder="Enter Facebook username"
            value={formData.facebook || ""}
            onChange={handleFormChange}
            required={false}
          />
        </div>
      </div>

      {/* Camera Modal */}
      {isCameraOpen && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-90 z-50 transition-all">
          <div className="bg-white rounded-2xl shadow-2xl p-6 w-full max-w-2xl relative overflow-hidden">
            <h3 className="text-2xl font-bold text-center text-gray-800 mb-2">
              📸 Take Your Photo
            </h3>
            <p className="text-gray-600 text-center mb-4">
              {facingMode === "user" 
                ? "Front Camera - Photo will be mirrored for natural appearance" 
                : "Back Camera - For highest quality photos"}
            </p>

            <div className="flex justify-center items-center gap-2 mb-4">
              <span className="text-sm text-gray-600">
                {facingMode === "user" ? "Front Camera" : "Back Camera"}
              </span>
              <button
                onClick={switchCamera}
                className="p-2 bg-gray-200 rounded-full hover:bg-gray-300 transition"
                title="Switch Camera"
              >
                <RotateCcw size={16} />
              </button>
            </div>

            <div className="relative rounded-xl overflow-hidden bg-black mx-auto max-w-lg">
              <video
                ref={videoRef}
                className={`w-full h-auto max-h-[60vh] object-contain rounded-lg ${
                  facingMode === "user" ? "scale-x-[-1]" : ""
                }`}
                autoPlay
                playsInline
                muted
              ></video>

              <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                <div className="w-64 h-80 border-2 border-white border-dashed rounded-lg opacity-40"></div>
              </div>

              <div className="absolute top-4 left-4 bg-black bg-opacity-50 text-white px-2 py-1 rounded text-sm">
                {facingMode === "environment" ? "📷 High Quality" : "🤳 Self Portrait"}
              </div>
            </div>

            <canvas
              ref={canvasRef}
              className="hidden"
            ></canvas>

            <div className="flex justify-center gap-4 mt-6">
              <button
                onClick={capturePhoto}
                className="px-8 py-3 bg-gradient-to-r from-green-500 to-emerald-600 text-white rounded-lg shadow-lg hover:scale-105 transition-transform duration-200 font-semibold flex items-center gap-2"
              >
                <Camera size={20} /> Capture Photo
              </button>
              <button
                onClick={cancelCamera}
                className="px-8 py-3 bg-gray-200 text-gray-800 rounded-lg shadow hover:bg-gray-300 hover:scale-105 transition-transform duration-200 font-semibold"
              >
                Cancel
              </button>
            </div>

            <div className="mt-4 text-center">
              <p className="text-sm text-gray-600 mb-2">
                💡 Tips for best results:
              </p>
              <div className="flex flex-wrap justify-center gap-4 text-xs text-gray-500">
                <span>Good lighting</span>
                <span>Steady hands</span>
                <span>Clear background</span>
                <span>Face the camera</span>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OtherDetailsForm;